import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "content-production-systems",
  kind: "horizontal",
  title: "Content Production Systems",
  shortTitle: "Content Production",
  status: "draft",
  tier: "we-do",
  seoTitle: "Content Production Systems | General Applications",
  seoDescription:
    "Content production systems for structured inputs, templates, generation, approval, provenance, rendering, and controlled delivery.",
  summary:
    "Content production systems that turn structured inputs, templates, source material, approval, rendering, and delivery into a repeatable process instead of a chain of manual reconstruction.",
  image: "/images/blog/navigating-ai-nuance-hero.webp",
  relatedCapabilitySlugs: ["intelligent-systems", "data-search-knowledge"],
  relatedIndustrySlugs: ["enterprise", "media-entertainment"],
  relatedPostSlugs: [
    "deterministic-controls-ai-agents",
    "navigating-ai-nuance",
    "multi-terabyte-ingest-choke",
  ],
  engineering: [
    "Structured source, template, content, derivative, and delivery models",
    "Workflow for creation, review, approval, revision, rendering, and release",
    "Rules-based, assisted, and AI-supported generation with explicit source context",
    "Provenance, version, validation, quality, and exception controls",
    "Integration with media libraries, technical documentation, data sources, and publishing channels",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Generation Is Only One Step",
    },
    {
      type: "paragraph",
      text: "Useful content production begins with the source material, audience, structure, and approval authority—not the tool that produces a first draft. Templates, rules, retrieval, automation, and AI can all help, but each needs a defined role in a process that preserves where a result came from and what still needs human judgment.",
    },
    {
      type: "heading",
      level: 2,
      text: "Make the Production Path Visible",
    },
    {
      type: "paragraph",
      text: "A production system should show what is being created, what source supports it, what version is under review, who can approve it, what was rendered or delivered, and where an exception belongs. That gives creative and operational teams a repeatable path without hiding important decisions inside a pile of documents, prompts, or messages.",
    },
  ],
  phases: {
    study: {
      focus:
        "Identify the content types, source material, decisions, handoffs, approvals, outputs, and failure modes that make production difficult today.",
      produces: [
        "Content, source, template, output, and workflow inventory",
        "Role, approval, version, provenance, and quality analysis",
        "Automation and AI suitability boundaries",
        "Publishing, rendering, delivery, and exception-path map",
      ],
      fit: {
        good: [
          "Teams repeatedly creating related content from structured sources, established templates, and clear approval responsibilities",
          "Organizations that need assisted generation to remain traceable to approved inputs, review, and controlled delivery",
        ],
        poor: [
          "A request to automate content before defining what makes an output correct, approved, attributable, or ready to release",
          "A process where no responsible owner can establish the source material or review consequential outputs",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the production state, source, template, generation, review, approval, rendering, and release model that fits the actual work.",
      produces: [
        "Content-production architecture and state model",
        "Source, template, provenance, and version design",
        "Generation, validation, approval, and exception boundaries",
        "Rendering, publishing, and delivery workflow plan",
      ],
    },
    "detailed-design": {
      focus:
        "Specify the inputs, interfaces, validation, integrations, and operational controls that make production repeatable without obscuring human responsibility.",
      produces: [
        "Content, template, source, and output specifications",
        "Review, approval, revision, and release procedures",
        "Generation, retrieval, validation, and provenance controls",
        "Publishing-channel, library, and delivery integration definitions",
      ],
    },
    implementation: {
      focus:
        "Build the production path around representative content and validate its outputs, evidence, handoffs, and exception behavior with the people responsible for release.",
      produces: [
        "Structured production, review, approval, and release workflows",
        "Traceable source, template, version, and output records",
        "Validated generation and rendering controls",
        "Operating guidance for ongoing content production",
      ],
    },
  },
};
